// Global logging configuration
// This file allows runtime configuration of log levels for different modules
// To use from browser console:
//   window.LoggingConfig.setGlobalLevel('warn')
//   window.LoggingConfig.setModuleLevel('preview-devtools', 'error')
//   window.LoggingConfig.showConfig()

import { setGlobalLogLevel, setModuleLogLevel, getLogConfig, LogLevel } from '../utils/logger'

class LoggingConfig {
  constructor() {
    // Set default log levels on initialization
    this.applyDefaultConfig()
  }
  
  applyDefaultConfig() {
    // Set global level to INFO to see informational messages
    setGlobalLogLevel(LogLevel.INFO)
    
    // Configure only the most verbose/repetitive modules to be quieter
    const quietModules = [
      'preview-devtools',
      'devtools-cable', 
      'dom-inspector',
      'network-monitor',
      'performance-metrics'
    ]
    
    quietModules.forEach(module => {
      setModuleLogLevel(module, LogLevel.WARN)
    })
    
    // Keep console-log at INFO to see important console messages
    const normalModules = [
      'console-log',
      'execution-progress',
      'error-tracker',
      'automation',
      'claude-code',
      'task-execution'
    ]
    
    normalModules.forEach(module => {
      setModuleLogLevel(module, LogLevel.INFO)
    })
  }
  
  // Helper methods for browser console
  setGlobalLevel(level) {
    const levelMap = {
      'debug': LogLevel.DEBUG,
      'info': LogLevel.INFO,
      'warn': LogLevel.WARN,
      'error': LogLevel.ERROR,
      'none': LogLevel.NONE
    }
    
    const logLevel = levelMap[level.toLowerCase()]
    if (logLevel !== undefined) {
      setGlobalLogLevel(logLevel)
      console.log(`Global log level set to: ${level}`)
    } else {
      console.error(`Invalid log level: ${level}. Use: debug, info, warn, error, or none`)
    }
  }
  
  setModuleLevel(module, level) {
    const levelMap = {
      'debug': LogLevel.DEBUG,
      'info': LogLevel.INFO,
      'warn': LogLevel.WARN,
      'error': LogLevel.ERROR,
      'none': LogLevel.NONE
    }
    
    const logLevel = levelMap[level.toLowerCase()]
    if (logLevel !== undefined) {
      setModuleLogLevel(module, logLevel)
      console.log(`Module '${module}' log level set to: ${level}`)
    } else {
      console.error(`Invalid log level: ${level}. Use: debug, info, warn, error, or none`)
    }
  }
  
  enableDebug() {
    setGlobalLogLevel(LogLevel.DEBUG)
    console.log('Debug logging enabled globally')
  }
  
  disableDebug() {
    this.applyDefaultConfig()
    console.log('Debug logging disabled - default config applied')
  }
  
  quietMode() {
    // Quick switch to quiet mode (only warnings and errors)
    setGlobalLogLevel(LogLevel.WARN)
    console.log('🔇 Quiet mode enabled - only warnings and errors will be shown')
  }
  
  verboseMode() {
    // Quick switch to verbose mode (all info messages)
    this.applyDefaultConfig()
    console.log('📢 Verbose mode enabled - INFO level messages will be shown')
  }
  
  showConfig() {
    const config = getLogConfig()
    const levelNames = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'NONE']
    
    console.log('=== Current Logging Configuration ===')
    console.log(`Global Level: ${levelNames[config.globalLevel]}`)
    console.log('Module Levels:')
    
    Object.entries(config.modules).forEach(([module, level]) => {
      console.log(`  ${module}: ${levelNames[level]}`)
    })
    
    console.log('\nTo change levels:')
    console.log('  window.LoggingConfig.setGlobalLevel("warn")')
    console.log('  window.LoggingConfig.setModuleLevel("preview-devtools", "error")')
    console.log('  window.LoggingConfig.enableDebug() // Enable all debug')
    console.log('  window.LoggingConfig.disableDebug() // Restore defaults')
  }
}

// Create singleton instance
const loggingConfig = new LoggingConfig()

// Export for use in other modules
export default loggingConfig

// Make available on window for runtime configuration
if (typeof window !== 'undefined') {
  window.LoggingConfig = loggingConfig
  
  // Show helpful message about logging configuration
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // Only show in development
    console.log('%c📋 Logging Config: INFO level (balanced mode)', 'color: #2196F3; font-size: 12px')
    console.log('%cUse LoggingConfig.showConfig() for details or LoggingConfig.setGlobalLevel("warn") for quieter output', 'color: #757575; font-size: 11px')
  }
}